import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { classnames } from 'Utils/classnames';

const Popover = ({
  children,
  content,
  position = 'bottom-right',
  openOnHover = false,
  openOnClick = true,
  offset = 8,
  className,
  isOpen: controlledOpen,
  onOpenChange,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = typeof controlledOpen === 'boolean';
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);

  const setOpen = useCallback(next => {
    if (isControlled) {
      onOpenChange && onOpenChange(next);
    } else {
      setUncontrolledOpen(next);
    }
  }, [isControlled, onOpenChange]);

  const handleDocumentClick = useCallback(e => {
    if (!wrapperRef.current) return;
    if (!wrapperRef.current.contains(e.target)) {
      setOpen(false);
    }
  }, [setOpen]);

  useEffect(() => {
    if (!openOnClick) return;
    if (isOpen) {
      document.addEventListener('mousedown', handleDocumentClick);
      return () => document.removeEventListener('mousedown', handleDocumentClick);
    }
  }, [isOpen, openOnClick, handleDocumentClick]);

  const triggerProps = useMemo(() => {
    const props = {};
    if (openOnHover) {
      props.onMouseEnter = () => setOpen(true);
      props.onMouseLeave = () => setOpen(false);
      props.onFocus = () => setOpen(true);
      props.onBlur = () => setOpen(false);
    }
    if (openOnClick) {
      props.onClick = e => {
        e.stopPropagation();
        setOpen(!isOpen);
      };
    }
    return props;
  }, [openOnHover, openOnClick, setOpen, isOpen]);

  return (
    <div
      ref={ wrapperRef }
      className={ classnames({
        popover: true,
        [className]: !!className,
      }) }
      style={ { '--popover-offset': `${offset}px` } }
    >
      <div className="popover__trigger" ref={ triggerRef } { ...triggerProps }>
        { children }
      </div>
      <div
        className={ classnames({
          popover__content: true,
          open: isOpen,
          [`pos-${position}`]: true,
        }) }
        role="tooltip"
      >
        <div className="popover__inner">
          { content }
        </div>
      </div>
    </div>
  );
};

export default Popover;

Popover.propTypes = {
  children: PropTypes.node.isRequired,
  content: PropTypes.node,
  position: PropTypes.oneOf([
    'top',
    'top-left',
    'top-right',
    'bottom',
    'bottom-left',
    'bottom-right',
    'left',
    'right',
  ]),
  openOnHover: PropTypes.bool,
  openOnClick: PropTypes.bool,
  offset: PropTypes.number,
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
};
